import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { EnablingsConfig } from '@core/models/built/procedure-step-config.model';
import { SessionStepValueType } from '@core/models/built/session-step-value-type.enum';
import { SessionStep, SessionStepValue } from '@core/models/session.model';
import { User } from '@core/models/users.model';
import { UsersCoreService } from '@core/services/features/users-core.service';
import { coreSelectors } from '@core/store/core.selectors';
import { TranslocoService } from '@ngneat/transloco';
import { Store } from '@ngrx/store';
import { filterTruthy } from '@utils';
import { DateTime } from 'luxon';
import { BehaviorSubject, Subscription } from 'rxjs';
import { first, map, withLatestFrom } from 'rxjs/operators';

import it from './i18n/explicit-user-enablings-session.it.json';

@Component({
  templateUrl: './explicit-user-enablings-session.component.html'
})
export class ExplicitUserEnablingsSessionComponent implements OnInit, OnChanges, OnDestroy {

  @Input() step?: SessionStep;

  @Output() commitAndNext = new EventEmitter<SessionStep>();

  private users$$ = new BehaviorSubject<User[]>([]);
  users$ = this.users$$.asObservable();

  private step$$ = new BehaviorSubject<SessionStep | undefined>(undefined);
  private step$ = this.step$$.pipe(filterTruthy());

  inputForm$ = this.step$.pipe(
    map(step => (step.procedureStep?.config as EnablingsConfig).enablingConfigs),
    filterTruthy(),
    map(enablingConfigs => {
      const form = this.fb.group({
        userId: [undefined, Validators.required],
        enablings: this.fb.array(enablingConfigs.map(e =>
          this.fb.group({
            enablingId: [e.enablingId, Validators.required],
            enabled: [false],
            note: [''],
            duration: this.fb.group({
              years: [e.duration.years || 0, Validators.required],
              months: [e.duration.months || 0, Validators.required],
              days: [e.duration.days || 0, Validators.required],
              hours: [e.duration.hours || 0, Validators.required],
              minutes: [e.duration.minutes || 0, Validators.required],
              seconds: [e.duration.seconds || 0, Validators.required],
            })
          })
        ) || [])
      });
      return form;
    })
  );

  enablingsControls$ = this.inputForm$.pipe(
    map(form => (form.get('enablings') as FormArray)?.controls)
  );

  private subs = new Subscription();

  constructor(
    private fb: FormBuilder,
    private usersSvc: UsersCoreService,
    private translocoSvc: TranslocoService,
    private store: Store
  ) {
    this.translocoSvc.setTranslation(it, 'it');
  }

  ngOnInit() {
    this.usersSvc.getListByRoleOperator().pipe(
      first(),
      withLatestFrom(this.store.select(coreSelectors.currentUser)),
      map(([users, currentUser]) => users.filter(u => u.id !== currentUser?.id))
    ).subscribe(users => this.users$$.next(users));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.step) {
      this.step$$.next(changes.step.currentValue);
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  submit(form: FormGroup) {
    this.step$.pipe(
      first()
    ).subscribe((step) => {
      if (!form.invalid) {
        const now = DateTime.local();
        const { userId, enablings } = form.value;
        const values: SessionStepValue[] = enablings.map((e: any) => ({
          workflowId: step.workflowId,
          workflowStepId: step.workflowStepId,
          sessionId: step.sessionId,
          timestamp: now.toJSDate(),
          type: SessionStepValueType.userEnabling,
          value: { ...e, userId }
        }));
        this.commitAndNext.emit({ ...step, completedOn: now.toJSDate(), values });
      }
    });
  }
}
